import React, { useState, useEffect, useCallback } from 'react';
import ReactDOM from 'react-dom';
import { Building2, Wrench, Disc3, Users, Search, X, ExternalLink, MapPin, ChevronUp, Loader2, RefreshCw, ChevronDown, Tag, Navigation, Star, MapPinned, Import, Globe, Heart, Share2, Music } from 'lucide-react';
import WishlistStar from '../../WishlistStar';

const DirectoryResourcesSection = () => {
  const [activeCategory, setActiveCategory] = useState('venues');
  const [resources, setResources] = useState([]);
  const [loading, setLoading] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [showAddModal, setShowAddModal] = useState(false);
  const [showSuggestModal, setShowSuggestModal] = useState(false);
  const [categoryCounts, setCategoryCounts] = useState({});
  const [currentUser, setCurrentUser] = useState(null);
  const [sortBy, setSortBy] = useState('featured-first');
  const [genreFilter, setGenreFilter] = useState('all');
  const [venueFilter, setVenueFilter] = useState('all');
  const [radiusFilter, setRadiusFilter] = useState('all');
  const [userLocation, setUserLocation] = useState(null);
  const [locationError, setLocationError] = useState(null);
  const [gettingLocation, setGettingLocation] = useState(false);
  const [manualLocationInput, setManualLocationInput] = useState('');
  
  // Google Places state
  const [showGooglePlaces, setShowGooglePlaces] = useState(false);
  const [googlePlaces, setGooglePlaces] = useState([]);
  const [googleLoading, setGoogleLoading] = useState(false);
  const [googlePlaceType, setGooglePlaceType] = useState('all');
  const [googleRadius, setGoogleRadius] = useState(16093); // 10 miles default
  const [googleSortBy, setGoogleSortBy] = useState('closest'); // Sort for Google Places results

  const API = process.env.REACT_APP_BACKEND_URL || '';

  // Categories configuration
  // Note: Record Labels moved to Producer's Corner > Label Connect
  const categories = [
    { id: 'venues', name: 'Venues', icon: Building2, color: '#ff6b6b', description: 'Clubs, bars, lounges, and event spaces' },
    { id: 'studios', name: 'Recording Studios', icon: Music, color: '#a78bfa', description: 'Recording studios, production facilities, and mixing spaces' },
    { id: 'record_shops', name: 'Record Shops', icon: Disc3, color: '#ffe66d', description: 'Vinyl stores, digital retailers, and record pools' },
    { id: 'communities', name: 'Communities', icon: Users, color: '#ff00ff', description: 'Forums, Discord servers, and groups' },
    { id: 'services', name: 'Services', icon: Wrench, color: '#4ecdc4', description: 'Education, rentals, and pro services' }
  ];

  // Sort options
  const sortOptions = [
    { value: 'featured-first', label: 'Featured First' },
    { value: 'featured', label: 'Featured Only' },
    { value: 'a-z', label: 'A-Z' },
    { value: 'z-a', label: 'Z-A' },
    { value: 'upvotes', label: 'Most Upvoted' }
  ];

  // Genre filter options (EDM focused)
  const genreOptions = [
    { value: 'all', label: 'All Genres' },
    { value: 'techno', label: 'Techno' },
    { value: 'house', label: 'House' },
    { value: 'tech-house', label: 'Tech House' },
    { value: 'deep-house', label: 'Deep House' },
    { value: 'trance', label: 'Trance' },
    { value: 'drum and bass', label: 'Drum & Bass' },
    { value: 'electronic', label: 'Electronic' },
    { value: 'edm', label: 'EDM' }
  ];

  // Venue subcategory filters
  const venueFilters = [
    { value: 'all', label: 'All Venues' },
    { value: 'nightclub', label: 'Nightclubs' },
    { value: 'bar', label: 'Bars' },
    { value: 'lounge', label: 'Lounges' },
    { value: 'event_space', label: 'Event Spaces' }
  ];

  // Radius filter options (miles)
  const radiusOptions = [
    { value: 'all', label: 'Any Distance' },
    { value: '5', label: 'Within 5 miles' },
    { value: '10', label: 'Within 10 miles' },
    { value: '25', label: 'Within 25 miles' },
    { value: '50', label: 'Within 50 miles' }
  ];

  // Dropdown style for dark background
  const selectStyle = {
    padding: '0.6rem 2rem 0.6rem 0.75rem',
    borderRadius: '8px',
    border: '1px solid rgba(255, 255, 255, 0.15)',
    background: '#1a1a2e',
    color: '#fff',
    fontSize: '0.85rem',
    outline: 'none',
    cursor: 'pointer',
    appearance: 'none',
    WebkitAppearance: 'none',
    MozAppearance: 'none'
  };

  // Get user's current location with fallback
  const getUserLocation = useCallback(() => {
    if (!navigator.geolocation) {
      setLocationError('Geolocation not supported');
      alert('Your browser does not support location services. Please use a modern browser.');
      return;
    }

    setGettingLocation(true);
    setLocationError(null);

    const handleSuccess = (position) => {
      setUserLocation({
        lat: position.coords.latitude,
        lng: position.coords.longitude
      });
      setGettingLocation(false);
      setLocationError(null);
    };

    const handleError = (error, isRetry = false) => {
      // If high accuracy failed and this isn't a retry, try with low accuracy
      if (!isRetry && error.code === error.TIMEOUT) {
        console.log('High accuracy timed out, trying low accuracy...');
        navigator.geolocation.getCurrentPosition(
          handleSuccess,
          (retryError) => handleError(retryError, true),
          { enableHighAccuracy: false, timeout: 15000, maximumAge: 300000 }
        );
        return;
      }

      setGettingLocation(false);
      let errorMessage = 'Unable to get location';
      
      switch (error.code) {
        case error.PERMISSION_DENIED:
          errorMessage = 'Location access denied. Please enable location services in your browser settings and refresh the page.';
          break;
        case error.POSITION_UNAVAILABLE:
          errorMessage = 'Location information unavailable. Please check your device settings.';
          break;
        case error.TIMEOUT:
          errorMessage = 'Location request timed out. Please check your connection and try again.';
          break;
        default:
          errorMessage = 'An error occurred getting your location.';
      }
      
      setLocationError(errorMessage);
      alert(errorMessage);
      console.error('Geolocation error:', error);
    };

    // First try with high accuracy, longer timeout
    navigator.geolocation.getCurrentPosition(
      handleSuccess,
      handleError,
      { enableHighAccuracy: true, timeout: 15000, maximumAge: 60000 }
    );
  }, []);

  // Geocode a manual location (city/zip) using Google Geocoding via backend
  const geocodeManualLocation = useCallback(async () => {
    if (!manualLocationInput.trim()) {
      alert('Please enter a city name or zip code');
      return;
    }

    setGettingLocation(true);
    setLocationError(null);

    try {
      const response = await fetch(`${API}/api/directory/geocode?address=${encodeURIComponent(manualLocationInput)}`);
      
      if (response.ok) {
        const data = await response.json();
        if (data.latitude && data.longitude) {
          setUserLocation({
            lat: data.latitude,
            lng: data.longitude
          });
          setLocationError(null);
        } else {
          const msg = data.reason === 'service_unavailable'
            ? (data.error || 'Location search is temporarily unavailable. Please try again later.')
            : 'Could not find that location. Try a different city or zip code.';
          setLocationError(msg);
          alert(msg);
        }
      } else {
        setLocationError('Failed to geocode location');
        alert('Failed to find location. Please try again.');
      }
    } catch (error) {
      console.error('Geocoding error:', error);
      setLocationError('Failed to geocode location');
      alert('Failed to find location. Please try again.');
    } finally {
      setGettingLocation(false);
    }
  }, [API, manualLocationInput]);

  // Calculate distance between two coordinates (Haversine formula)
  const calculateDistance = (lat1, lon1, lat2, lon2) => {
    const R = 3959; // Earth's radius in miles
    const dLat = (lat2 - lat1) * Math.PI / 180;
    const dLon = (lon2 - lon1) * Math.PI / 180;
    const a = 
      Math.sin(dLat/2) * Math.sin(dLat/2) +
      Math.cos(lat1 * Math.PI / 180) * Math.cos(lat2 * Math.PI / 180) * 
      Math.sin(dLon/2) * Math.sin(dLon/2);
    const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1-a));
    return R * c;
  };

  // Get current user
  useEffect(() => {
    const userStr = localStorage.getItem('beatseek_user');
    if (userStr) {
      try {
        setCurrentUser(JSON.parse(userStr));
      } catch (e) {
        console.error('Failed to parse user:', e);
      }
    }
  }, []);

  // Fetch category counts
  useEffect(() => {
    const fetchCounts = async () => {
      try {
        const response = await fetch(`${API}/api/directory/categories`);
        if (response.ok) {
          const data = await response.json();
          setCategoryCounts(data.categories || {});
        }
      } catch (error) {
        console.error('Error fetching category counts:', error);
      }
    };
    fetchCounts();
  }, [API]);

  // Fetch resources for active category
  const fetchResources = useCallback(async () => {
    setLoading(true);
    try {
      let url = `${API}/api/directory/resources?category=${activeCategory}`;
      if (searchQuery) {
        url += `&search=${encodeURIComponent(searchQuery)}`;
      }
      
      const response = await fetch(url);
      if (response.ok) {
        const data = await response.json();
        let filteredResources = data.resources || [];
        
        // Apply venue subcategory filter
        if (activeCategory === 'venues' && venueFilter !== 'all') {
          filteredResources = filteredResources.filter(r => 
            r.subcategory?.toLowerCase() === venueFilter.toLowerCase() ||
            r.tags?.some(t => t.toLowerCase().includes(venueFilter.toLowerCase()))
          );
        }

        // Apply radius filter for venues
        if (activeCategory === 'venues' && radiusFilter !== 'all' && userLocation) {
          const maxDistance = parseInt(radiusFilter);
          filteredResources = filteredResources.filter(r => {
            if (r.latitude && r.longitude) {
              const distance = calculateDistance(
                userLocation.lat, userLocation.lng,
                r.latitude, r.longitude
              );
              r.distance = distance; // Store distance for display
              return distance <= maxDistance;
            }
            return false; // Exclude venues without coordinates when filtering by radius
          });
        }

        // Apply genre filter (FILTER not sort)
        if (genreFilter !== 'all') {
          const genre = genreFilter.toLowerCase();
          filteredResources = filteredResources.filter(r =>
            r.tags?.some(t => t.toLowerCase().includes(genre))
          );
        }

        // Apply featured filter
        if (sortBy === 'featured') {
          filteredResources = filteredResources.filter(r => r.is_featured);
        }
        
        // Apply sorting
        filteredResources = sortResources(filteredResources, sortBy);
        
        setResources(filteredResources);
      } else {
        setResources([]);
      }
    } catch (error) {
      console.error('Error fetching resources:', error);
      setResources([]);
    } finally {
      setLoading(false);
    }
  }, [API, activeCategory, searchQuery, sortBy, genreFilter, venueFilter, radiusFilter, userLocation]);

  // Sort resources
  const sortResources = (resources, sortType) => {
    const sorted = [...resources];
    
    switch (sortType) {
      case 'a-z':
        return sorted.sort((a, b) => a.name.localeCompare(b.name));
      case 'z-a':
        return sorted.sort((a, b) => b.name.localeCompare(a.name));
      case 'upvotes':
        return sorted.sort((a, b) => (b.upvotes || 0) - (a.upvotes || 0));
      case 'featured':
        // Already filtered, just sort by name
        return sorted.sort((a, b) => a.name.localeCompare(b.name));
      case 'featured-first':
      default:
        return sorted.sort((a, b) => {
          if (a.is_featured && !b.is_featured) return -1;
          if (!a.is_featured && b.is_featured) return 1;
          return (b.upvotes || 0) - (a.upvotes || 0);
        });
    }
  };

  useEffect(() => {
    fetchResources();
  }, [fetchResources]);

  // Handle radius filter change - get location if needed
  useEffect(() => {
    if (radiusFilter !== 'all' && !userLocation && !gettingLocation) {
      getUserLocation();
    }
  }, [radiusFilter, userLocation, gettingLocation, getUserLocation]);

  // Combined search: geocode zip (if provided) then search Google Places
  const searchGooglePlaces = useCallback(async () => {
    setGoogleLoading(true);
    
    try {
      let searchLat = userLocation?.lat;
      let searchLng = userLocation?.lng;
      
      // If there's a zip/location input, geocode it first
      if (manualLocationInput.trim()) {
        const geocodeResponse = await fetch(`${API}/api/directory/geocode?address=${encodeURIComponent(manualLocationInput.trim())}`);
        
        if (geocodeResponse.ok) {
          const geocodeData = await geocodeResponse.json();
          if (geocodeData.success && geocodeData.latitude && geocodeData.longitude) {
            searchLat = geocodeData.latitude;
            searchLng = geocodeData.longitude;
            // Update user location state
            setUserLocation({ lat: searchLat, lng: searchLng });
          } else {
            alert('Could not find that location. Try a different city or zip code.');
            setGoogleLoading(false);
            return;
          }
        } else {
          alert('Failed to find location. Please try again.');
          setGoogleLoading(false);
          return;
        }
      }
      
      // Check if we have coordinates to search
      if (!searchLat || !searchLng) {
        alert('Please enter a zip code or city to search.');
        setGoogleLoading(false);
        return;
      }
      
      // Determine place type based on category
      const placeTypeToUse = activeCategory === 'studios' ? 'record_label' : googlePlaceType;
      
      // Now search Google Places with the coordinates
      const url = `${API}/api/directory/places/search?latitude=${searchLat}&longitude=${searchLng}&radius=${googleRadius}&place_type=${placeTypeToUse}&query=${encodeURIComponent(manualLocationInput.trim() || '')}`;
      const response = await fetch(url);
      
      if (response.ok) {
        const data = await response.json();
        setGooglePlaces(data.results || []);
      } else {
        console.error('Google Places search failed');
        setGooglePlaces([]);
      }
    } catch (error) {
      console.error('Error searching Google Places:', error);
      setGooglePlaces([]);
    } finally {
      setGoogleLoading(false);
    }
  }, [API, userLocation, googleRadius, googlePlaceType, manualLocationInput, activeCategory]);

  // Import a Google Place to our directory (admin only)
  const handleImportPlace = async (placeId) => {
    if (!currentUser || currentUser.role !== 'admin') {
      alert('Admin access required to import places');
      return;
    }

    try {
      const token = localStorage.getItem('beatseek_token');
      const response = await fetch(`${API}/api/directory/places/import/${placeId}`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      if (response.ok) {
        const data = await response.json();
        alert(`Successfully imported: ${data.resource.name}`);
        // Refresh the directory
        fetchResources();
        // Refresh category counts
        const countsResponse = await fetch(`${API}/api/directory/categories`);
        if (countsResponse.ok) {
          const countsData = await countsResponse.json();
          setCategoryCounts(countsData.categories || {});
        }
      } else {
        const error = await response.json();
        alert(`Import failed: ${error.detail}`);
      }
    } catch (error) {
      console.error('Error importing place:', error);
      alert('Failed to import place');
    }
  };

  // Delete a resource from curated directory (admin only)
  const handleDeleteResource = async (resourceId) => {
    if (!currentUser || currentUser.role !== 'admin') {
      alert('Admin access required');
      return;
    }

    try {
      const token = localStorage.getItem('beatseek_token');
      const response = await fetch(`${API}/api/directory/resources/${resourceId}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      if (response.ok) {
        alert('Resource removed from directory');
        fetchResources();
        // Refresh category counts
        const countsResponse = await fetch(`${API}/api/directory/categories`);
        if (countsResponse.ok) {
          const countsData = await countsResponse.json();
          setCategoryCounts(countsData.categories || {});
        }
      } else {
        const error = await response.json();
        alert(`Delete failed: ${error.detail}`);
      }
    } catch (error) {
      console.error('Error deleting resource:', error);
      alert('Failed to delete resource');
    }
  };

  // Handle upvote
  const handleUpvote = async (resourceId) => {
    if (!currentUser) {
      alert('Please log in to upvote');
      return;
    }

    try {
      const token = localStorage.getItem('beatseek_token');
      const response = await fetch(`${API}/api/directory/resources/${resourceId}/upvote`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      
      if (response.ok) {
        fetchResources();
      }
    } catch (error) {
      console.error('Error upvoting:', error);
    }
  };

  const isAdmin = currentUser?.role === 'admin';

  return (
    <div style={{ padding: '1.5rem', color: '#fff', minHeight: '100%' }}>
      {/* Category Tabs */}
      <div style={{ 
        display: 'flex', 
        gap: '0.5rem', 
        marginBottom: '1.5rem',
        flexWrap: 'wrap',
        borderBottom: '2px solid rgba(255, 255, 255, 0.1)',
        paddingBottom: '1rem'
      }}>
        {categories.map((cat) => {
          const IconComponent = cat.icon;
          const isActive = activeCategory === cat.id;
          const count = categoryCounts[cat.id]?.count || 0;
          
          return (
            <button
              key={cat.id}
              onClick={() => {
                setActiveCategory(cat.id);
                setVenueFilter('all');
                setRadiusFilter('all');
                setGenreFilter('all');
              }}
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: '0.5rem',
                padding: '0.6rem 1rem',
                borderRadius: '10px',
                border: isActive ? `2px solid ${cat.color}` : '2px solid rgba(255, 255, 255, 0.1)',
                background: isActive ? `${cat.color}20` : 'rgba(255, 255, 255, 0.05)',
                color: isActive ? cat.color : 'rgba(255, 255, 255, 0.7)',
                cursor: 'pointer',
                transition: 'all 0.2s ease',
                fontSize: '0.85rem',
                fontWeight: isActive ? '600' : '400'
              }}
            >
              <IconComponent size={16} />
              <span>{cat.name}</span>
            </button>
          );
        })}
      </div>

      {/* Search Bar, Filters & Actions */}
      <div style={{ 
        display: 'flex', 
        gap: '0.75rem', 
        marginBottom: '1rem',
        flexWrap: 'wrap',
        alignItems: 'center'
      }}>
        {/* Search */}
        <div style={{ 
          flex: 1, 
          minWidth: '180px',
          position: 'relative' 
        }}>
          <Search 
            size={16} 
            style={{ 
              position: 'absolute', 
              left: '10px', 
              top: '50%', 
              transform: 'translateY(-50%)',
              color: 'rgba(255, 255, 255, 0.4)' 
            }} 
          />
          <input
            type="text"
            placeholder={`Search ${categories.find(c => c.id === activeCategory)?.name}...`}
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            style={{
              width: '100%',
              padding: '0.6rem 0.75rem 0.6rem 2.25rem',
              borderRadius: '8px',
              border: '1px solid rgba(255, 255, 255, 0.15)',
              background: '#1a1a2e',
              color: '#fff',
              fontSize: '0.85rem',
              outline: 'none',
              boxSizing: 'border-box'
            }}
          />
        </div>

        {/* Genre Filter */}
        <div style={{ position: 'relative' }}>
          <select
            value={genreFilter}
            onChange={(e) => setGenreFilter(e.target.value)}
            style={selectStyle}
          >
            {genreOptions.map(opt => (
              <option key={opt.value} value={opt.value} style={{ background: '#1a1a2e', color: '#fff' }}>{opt.label}</option>
            ))}
          </select>
          <ChevronDown size={14} style={{ 
            position: 'absolute', 
            right: '8px', 
            top: '50%', 
            transform: 'translateY(-50%)',
            pointerEvents: 'none',
            color: 'rgba(255, 255, 255, 0.5)'
          }} />
        </div>

        {/* Sort Dropdown */}
        <div style={{ position: 'relative' }}>
          <select
            value={sortBy}
            onChange={(e) => setSortBy(e.target.value)}
            style={selectStyle}
          >
            {sortOptions.map(opt => (
              <option key={opt.value} value={opt.value} style={{ background: '#1a1a2e', color: '#fff' }}>{opt.label}</option>
            ))}
          </select>
          <ChevronDown size={14} style={{ 
            position: 'absolute', 
            right: '8px', 
            top: '50%', 
            transform: 'translateY(-50%)',
            pointerEvents: 'none',
            color: 'rgba(255, 255, 255, 0.5)'
          }} />
        </div>

        {/* Refresh */}
        <button
          onClick={() => fetchResources()}
          style={{
            padding: '0.6rem',
            borderRadius: '8px',
            border: '1px solid rgba(0, 255, 255, 0.3)',
            background: 'rgba(0, 255, 255, 0.1)',
            color: '#00ffff',
            cursor: 'pointer',
            display: 'flex',
            alignItems: 'center'
          }}
          title="Refresh"
        >
          <RefreshCw size={16} className={loading ? 'animate-spin' : ''} />
        </button>

        {/* Add/Suggest Resource */}
        {currentUser && (
          <button
            onClick={() => isAdmin ? setShowAddModal(true) : setShowSuggestModal(true)}
            style={{
              padding: '0.5rem 0.75rem',
              borderRadius: '6px',
              border: 'none',
              background: 'transparent',
              color: isAdmin ? '#00ffff' : '#ff00ff',
              cursor: 'pointer',
              fontSize: '0.8rem',
              fontWeight: '500',
              textDecoration: 'underline',
              textUnderlineOffset: '2px'
            }}
          >
            {isAdmin ? '+ Add Resource' : 'Suggest Resource'}
          </button>
        )}
      </div>

      {/* Second row of filters for Venues */}
      {(activeCategory === 'venues' || activeCategory === 'studios') && (
        <div style={{ 
          display: 'flex', 
          gap: '0.75rem', 
          marginBottom: '1.5rem',
          flexWrap: 'wrap',
          alignItems: 'center'
        }}>
          {/* Venue Type Filter (only in Directory mode for venues) */}
          {!showGooglePlaces && activeCategory === 'venues' && (
            <div style={{ position: 'relative' }}>
              <select
                value={venueFilter}
                onChange={(e) => setVenueFilter(e.target.value)}
                style={selectStyle}
              >
                {venueFilters.map(f => (
                  <option key={f.value} value={f.value} style={{ background: '#1a1a2e', color: '#fff' }}>{f.label}</option>
                ))}
              </select>
              <ChevronDown size={14} style={{ 
                position: 'absolute', 
                right: '8px', 
                top: '50%', 
                transform: 'translateY(-50%)',
                pointerEvents: 'none',
                color: 'rgba(255, 255, 255, 0.5)'
              }} />
            </div>
          )}

          {/* Spacer */}
          <div style={{ flex: 1 }} />

          {/* Two mode buttons */}
          <div style={{ display: 'flex', gap: '0.5rem' }}>
            {/* Curated Directory Button */}
            <button
              onClick={() => setShowGooglePlaces(false)}
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: '0.4rem',
                padding: '0.5rem 0.75rem',
                borderRadius: '8px',
                border: !showGooglePlaces ? '2px solid #ff00ff' : '1px solid rgba(0, 255, 255, 0.3)',
                background: !showGooglePlaces ? 'rgba(255, 0, 255, 0.2)' : 'rgba(0, 255, 255, 0.05)',
                color: !showGooglePlaces ? '#ff00ff' : '#00ffff',
                cursor: 'pointer',
                fontSize: '0.8rem',
                fontWeight: '500',
                transition: 'all 0.2s ease'
              }}
            >
              <Building2 size={16} />
              Curated
            </button>

            {/* Nearby Button */}
            <button
              onClick={() => setShowGooglePlaces(true)}
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: '0.4rem',
                padding: '0.5rem 0.75rem',
                borderRadius: '8px',
                border: showGooglePlaces ? '2px solid #ff00ff' : '1px solid rgba(0, 255, 255, 0.3)',
                background: showGooglePlaces ? 'rgba(255, 0, 255, 0.2)' : 'rgba(0, 255, 255, 0.05)',
                color: showGooglePlaces ? '#ff00ff' : '#00ffff',
                cursor: 'pointer',
                fontSize: '0.8rem',
                fontWeight: '500',
                transition: 'all 0.2s ease'
              }}
            >
              <MapPinned size={16} />
              Nearby
            </button>
          </div>
        </div>
      )}

      {/* Google Places Search Section (for Venues and Studios) */}
      {(activeCategory === 'venues' || activeCategory === 'studios') && showGooglePlaces && (
        <div style={{
          background: 'rgba(0, 255, 255, 0.05)',
          border: '1px solid rgba(0, 255, 255, 0.2)',
          borderRadius: '12px',
          padding: '1rem',
          marginBottom: '1.5rem'
        }}>
          <div style={{ 
            display: 'flex', 
            alignItems: 'center', 
            gap: '1rem', 
            marginBottom: '1rem',
            flexWrap: 'wrap'
          }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
              <MapPinned size={18} style={{ color: '#00ffff' }} />
              <span style={{ color: '#00ffff', fontWeight: '600', fontSize: '0.9rem' }}>
                Find Nearby Venues
              </span>
              <span style={{ fontSize: '0.65rem', color: 'rgba(255, 255, 255, 0.4)', fontStyle: 'italic' }}>
                (powered by Google Places)
              </span>
            </div>

            {/* Place Type - Only show for venues */}
            {activeCategory === 'venues' && (
              <div style={{ position: 'relative' }}>
                <select
                  value={googlePlaceType}
                  onChange={(e) => setGooglePlaceType(e.target.value)}
                  style={selectStyle}
                >
                  <option value="all" style={{ background: '#1a1a2e', color: '#fff' }}>All Venues</option>
                  <option value="night_club" style={{ background: '#1a1a2e', color: '#fff' }}>Nightclubs</option>
                  <option value="bar" style={{ background: '#1a1a2e', color: '#fff' }}>Bars</option>
                  <option value="lounge" style={{ background: '#1a1a2e', color: '#fff' }}>Lounges</option>
                  <option value="event_venue" style={{ background: '#1a1a2e', color: '#fff' }}>Event Spaces</option>
                </select>
                <ChevronDown size={14} style={{ 
                  position: 'absolute', 
                  right: '8px', 
                  top: '50%', 
                  transform: 'translateY(-50%)',
                  pointerEvents: 'none',
                  color: 'rgba(255, 255, 255, 0.5)'
                }} />
              </div>
            )}

            {/* Search Radius */}
            <div style={{ position: 'relative' }}>
              <select
                value={googleRadius}
                onChange={(e) => setGoogleRadius(parseInt(e.target.value))}
                style={selectStyle}
              >
                <option value="8047" style={{ background: '#1a1a2e', color: '#fff' }}>Within 5 miles</option>
                <option value="16093" style={{ background: '#1a1a2e', color: '#fff' }}>Within 10 miles</option>
                <option value="40234" style={{ background: '#1a1a2e', color: '#fff' }}>Within 25 miles</option>
                <option value="50000" style={{ background: '#1a1a2e', color: '#fff' }}>Within 30 miles (max)</option>
              </select>
              <ChevronDown size={14} style={{ 
                position: 'absolute', 
                right: '8px', 
                top: '50%', 
                transform: 'translateY(-50%)',
                pointerEvents: 'none',
                color: 'rgba(255, 255, 255, 0.5)'
              }} />
            </div>

            {/* Sort Dropdown for Google Places */}
            <div style={{ position: 'relative' }}>
              <select
                value={googleSortBy}
                onChange={(e) => setGoogleSortBy(e.target.value)}
                style={selectStyle}
              >
                <option value="closest" style={{ background: '#1a1a2e', color: '#fff' }}>Closest First</option>
                <option value="rating" style={{ background: '#1a1a2e', color: '#fff' }}>Highest Rated</option>
                <option value="a-z" style={{ background: '#1a1a2e', color: '#fff' }}>A-Z</option>
                <option value="z-a" style={{ background: '#1a1a2e', color: '#fff' }}>Z-A</option>
              </select>
              <ChevronDown size={14} style={{ 
                position: 'absolute', 
                right: '8px', 
                top: '50%', 
                transform: 'translateY(-50%)',
                pointerEvents: 'none',
                color: 'rgba(255, 255, 255, 0.5)'
              }} />
            </div>
          </div>

          {/* Zip Code Input + Search */}
          <div style={{ 
            display: 'flex', 
            alignItems: 'center', 
            gap: '0.5rem', 
            marginBottom: '1rem',
            padding: '0.75rem',
            background: 'rgba(0, 255, 255, 0.03)',
            borderRadius: '8px',
            border: '1px solid rgba(0, 255, 255, 0.15)'
          }}>
            <MapPin size={16} style={{ color: '#00ffff', flexShrink: 0 }} />
            <input
              type="text"
              value={manualLocationInput}
              onChange={(e) => setManualLocationInput(e.target.value)}
              onKeyDown={(e) => e.key === 'Enter' && searchGooglePlaces()}
              placeholder="Enter zip code or city to search..."
              style={{
                flex: 1,
                padding: '0.5rem 0.75rem',
                borderRadius: '6px',
                border: '1px solid rgba(255, 255, 255, 0.15)',
                background: '#1a1a2e',
                color: '#fff',
                fontSize: '0.85rem',
                outline: 'none'
              }}
            />
            <button
              onClick={searchGooglePlaces}
              disabled={googleLoading || !manualLocationInput.trim()}
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: '0.4rem',
                padding: '0.5rem 1rem',
                borderRadius: '6px',
                border: 'none',
                background: googleLoading || !manualLocationInput.trim() 
                  ? 'rgba(0, 255, 255, 0.3)' 
                  : 'linear-gradient(135deg, #00ffff, #0080ff)',
                color: googleLoading || !manualLocationInput.trim() ? 'rgba(0,0,0,0.5)' : '#000',
                cursor: googleLoading || !manualLocationInput.trim() ? 'not-allowed' : 'pointer',
                fontSize: '0.85rem',
                fontWeight: '600'
              }}
              title="Click to search or refresh results"
            >
              {googleLoading ? (
                <Loader2 size={16} className="animate-spin" />
              ) : (
                <>
                  <Search size={14} />
                  <RefreshCw size={12} style={{ marginLeft: '-2px' }} />
                </>
              )}
              {googlePlaces.length > 0 ? 'Refresh' : 'Search'}
            </button>
          </div>

          {/* Helper tip about radius */}
          <p style={{ 
            fontSize: '0.7rem', 
            color: 'rgba(255, 255, 255, 0.4)', 
            marginBottom: '1rem',
            fontStyle: 'italic'
          }}>
            💡 Tip: Google limits searches to ~30 miles. For larger areas, search multiple nearby cities or zip codes.
          </p>

          {/* Google Places Results */}
          {googleLoading ? (
            <div style={{ 
              display: 'flex', 
              justifyContent: 'center', 
              padding: '2rem',
              color: 'rgba(255, 255, 255, 0.5)'
            }}>
              <Loader2 size={24} className="animate-spin" style={{ marginRight: '0.5rem' }} />
              Searching nearby venues...
            </div>
          ) : googlePlaces.length > 0 ? (
            <div style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))',
              gap: '1rem'
            }}>
              {/* Sort and render Google Places */}
              {[...googlePlaces]
                .map(place => ({
                  ...place,
                  distance: userLocation && place.latitude && place.longitude 
                    ? calculateDistance(userLocation.lat, userLocation.lng, place.latitude, place.longitude)
                    : null
                }))
                .sort((a, b) => {
                  switch (googleSortBy) {
                    case 'a-z':
                      return a.name.localeCompare(b.name);
                    case 'z-a':
                      return b.name.localeCompare(a.name);
                    case 'rating':
                      return (b.rating || 0) - (a.rating || 0);
                    case 'closest':
                    default:
                      if (a.distance === null) return 1;
                      if (b.distance === null) return -1;
                      return a.distance - b.distance;
                  }
                })
                .map((place) => (
                  <GooglePlaceCard 
                    key={place.id} 
                    place={place} 
                    isAdmin={isAdmin}
                    currentUser={currentUser}
                    onImport={handleImportPlace}
                    showDistance={googleSortBy === 'closest' && place.distance !== null}
                  />
                ))}
            </div>
          ) : (
            <div style={{ 
              textAlign: 'center', 
              padding: '2rem',
              color: 'rgba(255, 255, 255, 0.5)'
            }}>
              {userLocation ? 'Click "Search" to find nearby venues' : 'Enter a zip code or city above to begin searching'}
            </div>
          )}
        </div>
      )}

      {/* Category Description */}
      <p style={{ 
        color: 'rgba(255, 255, 255, 0.5)', 
        marginBottom: '1.5rem',
        fontSize: '0.85rem'
      }}>
        {categories.find(c => c.id === activeCategory)?.description}
        {genreFilter !== 'all' && ` • Filtered by: ${genreOptions.find(g => g.value === genreFilter)?.label}`}
      </p>

      {/* Resources Grid - Show when not in Google Places mode OR for non-venue/studio categories */}
      {(!showGooglePlaces || (activeCategory !== 'venues' && activeCategory !== 'studios')) && (
        <>
          {loading ? (
            <div style={{ 
              display: 'flex', 
              justifyContent: 'center', 
              alignItems: 'center',
              padding: '4rem',
              color: 'rgba(255, 255, 255, 0.5)'
            }}>
              <Loader2 size={28} className="animate-spin" style={{ marginRight: '0.75rem' }} />
              Loading resources...
            </div>
          ) : resources.length === 0 ? (
            <div style={{ 
              textAlign: 'center', 
              padding: '4rem',
              color: 'rgba(255, 255, 255, 0.5)'
            }}>
              <Disc3 size={40} style={{ marginBottom: '1rem', opacity: 0.3 }} />
              <p style={{ fontSize: '1rem', marginBottom: '0.5rem' }}>No resources found</p>
              <p style={{ fontSize: '0.85rem' }}>
                {genreFilter !== 'all' ? 'Try selecting a different genre filter.' :
                 radiusFilter !== 'all' && !userLocation ? 'Enable location access to filter by distance.' :
                 radiusFilter !== 'all' ? 'No venues found within this radius.' :
                 currentUser ? 'Be the first to suggest a resource for this category!' : 
                 'Log in to suggest resources for this category.'}
              </p>
            </div>
          ) : (
            <div style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(auto-fill, minmax(300px, 1fr))',
              gap: '1rem'
            }}>
              {resources.map((resource) => (
                <ResourceCard 
                  key={resource.id} 
                  resource={resource} 
                  onUpvote={handleUpvote}
                  onDelete={handleDeleteResource}
                  currentUser={currentUser}
                  isAdmin={isAdmin}
                  categoryColor={categories.find(c => c.id === activeCategory)?.color || '#00ffff'}
                  showDistance={activeCategory === 'venues' && radiusFilter !== 'all'}
                />
              ))}
            </div>
          )}
        </>
      )}

      {/* Add Resource Modal (Admin) */}
      {showAddModal && (
        <AddResourceModal
          category={activeCategory}
          categories={categories}
          onClose={() => setShowAddModal(false)}
          onSuccess={() => {
            setShowAddModal(false);
            fetchResources();
          }}
        />
      )}

      {/* Suggest Resource Modal (Users) */}
      {showSuggestModal && (
        <SuggestResourceModal
          category={activeCategory}
          categories={categories}
          onClose={() => setShowSuggestModal(false)}
          onSuccess={() => {
            setShowSuggestModal(false);
            alert('Thank you! Your suggestion has been submitted for review.');
          }}
        />
      )}
    </div>
  );
};

// Google Place Card Component (for nearby search results)
const GooglePlaceCard = ({ place, isAdmin, onImport, showDistance, currentUser, onSave, onShare }) => {
  const [isHovered, setIsHovered] = useState(false);
  const [isSaved, setIsSaved] = useState(false);
  const [saving, setSaving] = useState(false);

  const API = process.env.REACT_APP_BACKEND_URL || '';

  // Handle save/unsave
  const handleSave = async () => {
    if (!currentUser) {
      alert('Please log in to save venues');
      return;
    }

    const token = localStorage.getItem('token');
    if (!token) {
      alert('Please log in to save venues');
      return;
    }

    setSaving(true);
    try {
      if (isSaved) {
        // Unsave
        const response = await fetch(`${API}/api/directory/venues/save/${place.id}`, {
          method: 'DELETE',
          headers: { 'Authorization': `Bearer ${token}` }
        });
        if (response.ok) {
          setIsSaved(false);
        }
      } else {
        // Save
        const response = await fetch(`${API}/api/directory/venues/save`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${token}`
          },
          body: JSON.stringify({
            google_place_id: place.id,
            name: place.name,
            address: place.address,
            photo_url: place.photo_url,
            rating: place.rating,
            google_maps_url: place.google_maps_url,
            website: place.website,
            primary_type: place.primary_type
          })
        });
        if (response.ok) {
          setIsSaved(true);
        }
      }
    } catch (error) {
      console.error('Save error:', error);
    } finally {
      setSaving(false);
    }
  };

  // Handle share - internal Beatseek sharing
  const handleShare = async () => {
    if (!currentUser) {
      alert('Please log in to share venues with the community');
      return;
    }

    const token = localStorage.getItem('token');
    if (!token) {
      alert('Please log in to share venues');
      return;
    }

    try {
      // Share to Beatseek community
      const response = await fetch(`${API}/api/directory/venues/share`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          google_place_id: place.id,
          name: place.name,
          address: place.address,
          photo_url: place.photo_url,
          rating: place.rating,
          google_maps_url: place.google_maps_url,
          website: place.website,
          primary_type: place.primary_type
        })
      });

      if (response.ok) {
        alert(`"${place.name}" shared with the Beatseek community! 🎉`);
      } else {
        const data = await response.json();
        alert(data.detail || 'Failed to share');
      }
    } catch (error) {
      console.error('Share error:', error);
      alert('Failed to share venue');
    }
  };

  return (
    <div
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
      style={{
        background: isHovered 
          ? 'linear-gradient(135deg, rgba(30, 41, 59, 0.95), rgba(15, 23, 42, 0.95))'
          : 'linear-gradient(135deg, rgba(15, 23, 42, 0.9), rgba(30, 41, 59, 0.9))',
        borderRadius: '12px',
        border: isHovered ? '2px solid rgba(0, 255, 255, 0.5)' : '2px solid rgba(255, 255, 255, 0.1)',
        overflow: 'hidden',
        transition: 'all 0.3s ease',
        transform: isHovered ? 'translateY(-2px)' : 'translateY(0)',
        display: 'flex',
        flexDirection: 'column'
      }}
    >
      {/* Photo */}
      {place.photo_url && (
        <div style={{ height: '120px', overflow: 'hidden', position: 'relative' }}>
          <img 
            src={place.photo_url} 
            alt={place.name}
            style={{ 
              width: '100%', 
              height: '100%', 
              objectFit: 'cover'
            }}
            onError={(e) => { e.target.style.display = 'none'; }}
          />
          {/* Distance badge on photo */}
          {showDistance && place.distance && (
            <div style={{
              position: 'absolute',
              top: '8px',
              right: '8px',
              background: 'rgba(0, 0, 0, 0.75)',
              color: '#00ffff',
              padding: '0.2rem 0.5rem',
              borderRadius: '4px',
              fontSize: '0.7rem',
              fontWeight: '600'
            }}>
              {place.distance.toFixed(1)} mi
            </div>
          )}
          {/* Save button overlay */}
          <button
            onClick={handleSave}
            disabled={saving}
            style={{
              position: 'absolute',
              top: '8px',
              left: '8px',
              background: 'rgba(0, 0, 0, 0.6)',
              border: 'none',
              borderRadius: '50%',
              width: '32px',
              height: '32px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              cursor: saving ? 'wait' : 'pointer',
              transition: 'all 0.2s ease'
            }}
            title={isSaved ? 'Remove from saved' : 'Save venue'}
          >
            <Heart 
              size={16} 
              fill={isSaved ? '#ff6b6b' : 'none'} 
              color={isSaved ? '#ff6b6b' : '#fff'}
            />
          </button>
        </div>
      )}

      <div style={{ padding: '0.875rem', flex: 1, display: 'flex', flexDirection: 'column' }}>
        {/* Header with name and rating */}
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '0.4rem' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '0.4rem', flex: 1, minWidth: 0 }}>
            <h3 style={{ 
              fontSize: '0.95rem', 
              fontWeight: '700', 
              color: '#fff',
              margin: 0,
              flex: 1,
              paddingRight: '0.3rem',
              lineHeight: '1.3',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap'
            }}>
              {place.name}
            </h3>
            <WishlistStar 
              itemType="venue"
              itemData={{
                id: place.id || place.google_place_id,
                name: place.name,
                location: place.address,
                url: place.website || place.google_maps_url,
                rating: place.rating,
                source: 'google_places'
              }}
              size={15}
            />
          </div>
          
          {place.rating && (
            <div style={{
              display: 'flex',
              alignItems: 'center',
              gap: '0.2rem',
              padding: '0.2rem 0.4rem',
              borderRadius: '6px',
              background: 'rgba(255, 193, 7, 0.2)',
              color: '#ffc107',
              fontSize: '0.75rem',
              fontWeight: '600'
            }}>
              <Star size={12} fill="#ffc107" />
              {place.rating.toFixed(1)}
            </div>
          )}
        </div>

        {/* Address */}
        <div style={{ 
          display: 'flex', 
          alignItems: 'flex-start', 
          gap: '0.3rem',
          marginBottom: '0.5rem',
          fontSize: '0.75rem',
          color: 'rgba(255, 255, 255, 0.6)',
          lineHeight: '1.3'
        }}>
          <MapPin size={12} style={{ flexShrink: 0, marginTop: '2px' }} />
          <span>{place.address}</span>
        </div>

        {/* Open status */}
        {place.is_open !== undefined && (
          <div style={{ 
            fontSize: '0.7rem', 
            color: place.is_open ? '#4ecdc4' : '#ff6b6b',
            marginBottom: '0.5rem'
          }}>
            {place.is_open ? '● Open Now' : '○ Closed'}
          </div>
        )}

        {/* Spacer */}
        <div style={{ flex: 1 }} />

        {/* Action buttons - two rows */}
        <div style={{ display: 'flex', flexDirection: 'column', gap: '0.4rem', marginTop: '0.5rem' }}>
          {/* First row: Maps & Website */}
          <div style={{ display: 'flex', gap: '0.4rem' }}>
            {place.google_maps_url && (
              <a
                href={place.google_maps_url}
                target="_blank"
                rel="noopener noreferrer"
                style={{
                  flex: 1,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: '0.25rem',
                  padding: '0.35rem 0.4rem',
                  borderRadius: '6px',
                  background: 'rgba(0, 255, 255, 0.1)',
                  border: '1px solid rgba(0, 255, 255, 0.3)',
                  color: '#00ffff',
                  textDecoration: 'none',
                  fontSize: '0.7rem',
                  fontWeight: '500'
                }}
              >
                <MapPin size={11} />
                Maps
              </a>
            )}
            
            {place.website && (
              <a
                href={place.website}
                target="_blank"
                rel="noopener noreferrer"
                style={{
                  flex: 1,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: '0.25rem',
                  padding: '0.35rem 0.4rem',
                  borderRadius: '6px',
                  background: 'rgba(255, 255, 255, 0.05)',
                  border: '1px solid rgba(255, 255, 255, 0.2)',
                  color: '#fff',
                  textDecoration: 'none',
                  fontSize: '0.7rem',
                  fontWeight: '500'
                }}
              >
                <ExternalLink size={11} />
                Site
              </a>
            )}
          </div>

          {/* Second row: Share & Import (admin) */}
          <div style={{ display: 'flex', gap: '0.4rem' }}>
            {/* Share button */}
            <button
              onClick={handleShare}
              style={{
                flex: 1,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                gap: '0.25rem',
                padding: '0.35rem 0.4rem',
                borderRadius: '6px',
                background: 'rgba(78, 205, 196, 0.1)',
                border: '1px solid rgba(78, 205, 196, 0.3)',
                color: '#4ecdc4',
                cursor: 'pointer',
                fontSize: '0.7rem',
                fontWeight: '500'
              }}
              title="Share with Beatseek community"
            >
              <Share2 size={11} />
              Share
            </button>

            {isAdmin && (
              <button
                onClick={() => onImport(place.id)}
                style={{
                  flex: 1,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: '0.25rem',
                  padding: '0.35rem 0.4rem',
                  borderRadius: '6px',
                  background: 'rgba(255, 0, 255, 0.1)',
                  border: '1px solid rgba(255, 0, 255, 0.3)',
                  color: '#ff00ff',
                  cursor: 'pointer',
                  fontSize: '0.7rem',
                  fontWeight: '500'
                }}
                title="Import to Curated Directory"
              >
                <Import size={11} />
                Import
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

// Resource Card Component
const ResourceCard = ({ resource, onUpvote, onDelete, currentUser, isAdmin, categoryColor, showDistance }) => {
  const [isHovered, setIsHovered] = useState(false);

  return (
    <div
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
      style={{
        background: isHovered 
          ? 'linear-gradient(135deg, rgba(30, 41, 59, 0.95), rgba(15, 23, 42, 0.95))'
          : 'linear-gradient(135deg, rgba(15, 23, 42, 0.9), rgba(30, 41, 59, 0.9))',
        borderRadius: '14px',
        border: isHovered ? `2px solid ${categoryColor}60` : '2px solid rgba(255, 255, 255, 0.1)',
        overflow: 'hidden',
        transition: 'all 0.3s ease',
        transform: isHovered ? 'translateY(-3px)' : 'translateY(0)',
        boxShadow: isHovered ? `0 8px 24px ${categoryColor}15` : 'none',
        display: 'flex',
        flexDirection: 'column',
        height: '280px',
        position: 'relative'
      }}
    >
      {/* Admin Delete Button */}
      {isAdmin && onDelete && (
        <button
          onClick={(e) => {
            e.stopPropagation();
            if (window.confirm(`Remove "${resource.name}" from the directory?`)) {
              onDelete(resource.id);
            }
          }}
          style={{
            position: 'absolute',
            top: '8px',
            right: '8px',
            background: 'rgba(255, 0, 0, 0.8)',
            border: 'none',
            borderRadius: '50%',
            width: '24px',
            height: '24px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            cursor: 'pointer',
            zIndex: 10,
            opacity: isHovered ? 1 : 0,
            transition: 'opacity 0.2s ease'
          }}
          title="Remove from directory"
        >
          <X size={14} color="#fff" />
        </button>
      )}

      {/* Featured Badge */}
      {resource.is_featured && (
        <div style={{
          background: `linear-gradient(135deg, ${categoryColor}, ${categoryColor}80)`,
          color: '#000',
          padding: '0.2rem 0.6rem',
          fontSize: '0.65rem',
          fontWeight: '700',
          textTransform: 'uppercase',
          letterSpacing: '0.5px'
        }}>
          ⭐ Featured
        </div>
      )}

      <div style={{ 
        padding: '1rem', 
        flex: 1, 
        display: 'flex', 
        flexDirection: 'column'
      }}>
        {/* Header */}
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '0.5rem' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', flex: 1, minWidth: 0 }}>
            <h3 style={{ 
              fontSize: '1rem', 
              fontWeight: '700', 
              color: '#fff',
              margin: 0,
              flex: 1,
              paddingRight: '0.5rem',
              lineHeight: '1.3',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap'
            }}>
              {resource.name}
            </h3>
            <WishlistStar 
              itemType="venue"
              itemData={{
                id: resource.id,
                name: resource.name,
                location: resource.location,
                description: resource.description,
                url: resource.url || resource.website,
                category: resource.category
              }}
              size={16}
            />
          </div>
          
          {/* Upvote Button */}
          <button
            onClick={() => onUpvote(resource.id)}
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: '0.2rem',
              padding: '0.25rem 0.5rem',
              borderRadius: '6px',
              border: '1px solid rgba(255, 255, 255, 0.2)',
              background: 'rgba(255, 255, 255, 0.05)',
              color: 'rgba(255, 255, 255, 0.7)',
              cursor: currentUser ? 'pointer' : 'not-allowed',
              fontSize: '0.75rem',
              transition: 'all 0.2s ease',
              flexShrink: 0
            }}
            title={currentUser ? 'Upvote this resource' : 'Log in to upvote'}
          >
            <ChevronUp size={12} />
            <span>{resource.upvotes || 0}</span>
          </button>
        </div>

        {/* Description */}
        <p style={{ 
          fontSize: '0.8rem', 
          color: 'rgba(255, 255, 255, 0.6)',
          marginBottom: '0.5rem',
          lineHeight: '1.4',
          display: '-webkit-box',
          WebkitLineClamp: 3,
          WebkitBoxOrient: 'vertical',
          overflow: 'hidden',
          flex: '0 0 auto',
          minHeight: '50px'
        }}>
          {resource.description}
        </p>

        {/* Location & Distance */}
        {resource.location && (
          <div style={{ 
            display: 'flex', 
            alignItems: 'center', 
            gap: '0.3rem',
            marginBottom: '0.5rem',
            fontSize: '0.75rem',
            color: 'rgba(255, 255, 255, 0.5)'
          }}>
            <MapPin size={12} />
            <span>{resource.location}</span>
            {showDistance && resource.distance && (
              <span style={{ color: '#4ecdc4', marginLeft: '0.5rem' }}>
                ({resource.distance.toFixed(1)} mi)
              </span>
            )}
          </div>
        )}

        {/* Tags */}
        {resource.tags && resource.tags.length > 0 && (
          <div style={{ 
            display: 'flex', 
            flexWrap: 'wrap', 
            gap: '0.3rem',
            marginBottom: '0.75rem',
            flex: 1
          }}>
            {resource.tags.slice(0, 3).map((tag, idx) => (
              <span
                key={idx}
                style={{
                  padding: '0.15rem 0.4rem',
                  borderRadius: '4px',
                  background: `${categoryColor}12`,
                  color: categoryColor,
                  fontSize: '0.65rem',
                  fontWeight: '500'
                }}
              >
                {tag}
              </span>
            ))}
          </div>
        )}

        {/* Spacer */}
        <div style={{ flex: 1 }} />

        {/* Visit Link - Always at bottom */}
        <a
          href={resource.external_url}
          target="_blank"
          rel="noopener noreferrer"
          style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            gap: '0.4rem',
            padding: '0.5rem 0.75rem',
            borderRadius: '8px',
            background: `linear-gradient(135deg, ${categoryColor}25, ${categoryColor}10)`,
            border: `1px solid ${categoryColor}40`,
            color: categoryColor,
            textDecoration: 'none',
            fontSize: '0.8rem',
            fontWeight: '600',
            transition: 'all 0.2s ease',
            marginTop: 'auto'
          }}
        >
          <ExternalLink size={14} />
          Visit Website
        </a>
      </div>
    </div>
  );
};

// Input style for modals
const inputStyle = {
  width: '100%',
  padding: '0.6rem',
  borderRadius: '8px',
  border: '1px solid rgba(255, 255, 255, 0.15)',
  background: '#1a1a2e',
  color: '#fff',
  fontSize: '0.85rem',
  boxSizing: 'border-box'
};

// Add Resource Modal (Admin Only)
const AddResourceModal = ({ category, categories, onClose, onSuccess }) => {
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    category: category,
    subcategory: '',
    external_url: '',
    location: '',
    latitude: '',
    longitude: '',
    tags: '',
    is_featured: false
  });
  const [loading, setLoading] = useState(false);

  const API = process.env.REACT_APP_BACKEND_URL || '';

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      const token = localStorage.getItem('beatseek_token');
      const tagsArray = formData.tags.split(',').map(t => t.trim()).filter(t => t);
      
      const submitData = {
        ...formData,
        tags: tagsArray,
        latitude: formData.latitude ? parseFloat(formData.latitude) : null,
        longitude: formData.longitude ? parseFloat(formData.longitude) : null
      };

      const response = await fetch(`${API}/api/directory/resources`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify(submitData)
      });

      if (response.ok) {
        onSuccess();
      } else {
        const error = await response.json();
        alert(`Error: ${error.detail}`);
      }
    } catch (error) {
      console.error('Error adding resource:', error);
      alert('Failed to add resource');
    } finally {
      setLoading(false);
    }
  };

  const getSubcategories = () => {
    switch (formData.category) {
      case 'venues':
        return ['nightclub', 'bar', 'lounge', 'festival', 'warehouse', 'rooftop'];
      case 'services':
        return ['education', 'rentals', 'photography', 'design', 'promotion'];
      case 'record_shops':
        return ['vinyl', 'digital', 'record_pool', 'marketplace'];
      case 'communities':
        return ['forum', 'discord', 'facebook', 'reddit', 'other'];
      default:
        return [];
    }
  };

  return ReactDOM.createPortal(
    <div
      style={{
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        background: 'rgba(0, 0, 0, 0.9)',
        backdropFilter: 'blur(10px)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        zIndex: 10003,
        padding: '1rem'
      }}
      onClick={onClose}
    >
      <div
        style={{
          maxWidth: '450px',
          width: '100%',
          background: 'rgba(26, 26, 46, 0.98)',
          borderRadius: '16px',
          border: '2px solid #00ffff',
          maxHeight: '85vh',
          overflow: 'auto'
        }}
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div style={{
          padding: '1rem 1.25rem',
          borderBottom: '1px solid rgba(0, 255, 255, 0.2)',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center'
        }}>
          <h2 style={{ margin: 0, color: '#00ffff', fontSize: '1.1rem' }}>
            Add New Resource
          </h2>
          <button
            onClick={onClose}
            style={{
              width: '32px',
              height: '32px',
              borderRadius: '50%',
              background: 'rgba(255, 0, 128, 0.2)',
              border: '1px solid rgba(255, 0, 128, 0.4)',
              color: '#ff0080',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              cursor: 'pointer'
            }}
          >
            <X size={16} />
          </button>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit} style={{ padding: '1.25rem' }}>
          <div style={{ marginBottom: '0.875rem' }}>
            <label style={{ display: 'block', marginBottom: '0.3rem', color: '#00ffff', fontSize: '0.8rem' }}>
              Category
            </label>
            <select
              value={formData.category}
              onChange={(e) => setFormData({ ...formData, category: e.target.value, subcategory: '' })}
              style={inputStyle}
            >
              {categories.map((cat) => (
                <option key={cat.id} value={cat.id} style={{ background: '#1a1a2e', color: '#fff' }}>{cat.name}</option>
              ))}
            </select>
          </div>

          {getSubcategories().length > 0 && (
            <div style={{ marginBottom: '0.875rem' }}>
              <label style={{ display: 'block', marginBottom: '0.3rem', color: '#00ffff', fontSize: '0.8rem' }}>
                Subcategory
              </label>
              <select
                value={formData.subcategory}
                onChange={(e) => setFormData({ ...formData, subcategory: e.target.value })}
                style={inputStyle}
              >
                <option value="" style={{ background: '#1a1a2e', color: '#fff' }}>Select subcategory</option>
                {getSubcategories().map((sub) => (
                  <option key={sub} value={sub} style={{ background: '#1a1a2e', color: '#fff' }}>
                    {sub.charAt(0).toUpperCase() + sub.slice(1).replace('_', ' ')}
                  </option>
                ))}
              </select>
            </div>
          )}

          <div style={{ marginBottom: '0.875rem' }}>
            <label style={{ display: 'block', marginBottom: '0.3rem', color: '#00ffff', fontSize: '0.8rem' }}>
              Name *
            </label>
            <input
              type="text"
              value={formData.name}
              onChange={(e) => setFormData({ ...formData, name: e.target.value })}
              required
              placeholder="e.g., Fabric London"
              style={inputStyle}
            />
          </div>

          <div style={{ marginBottom: '0.875rem' }}>
            <label style={{ display: 'block', marginBottom: '0.3rem', color: '#00ffff', fontSize: '0.8rem' }}>
              Description *
            </label>
            <textarea
              value={formData.description}
              onChange={(e) => setFormData({ ...formData, description: e.target.value })}
              required
              placeholder="Brief description..."
              rows={2}
              style={{ ...inputStyle, resize: 'vertical' }}
            />
          </div>

          <div style={{ marginBottom: '0.875rem' }}>
            <label style={{ display: 'block', marginBottom: '0.3rem', color: '#00ffff', fontSize: '0.8rem' }}>
              Website URL *
            </label>
            <input
              type="url"
              value={formData.external_url}
              onChange={(e) => setFormData({ ...formData, external_url: e.target.value })}
              required
              placeholder="https://..."
              style={inputStyle}
            />
          </div>

          <div style={{ marginBottom: '0.875rem' }}>
            <label style={{ display: 'block', marginBottom: '0.3rem', color: '#00ffff', fontSize: '0.8rem' }}>
              Location
            </label>
            <input
              type="text"
              value={formData.location}
              onChange={(e) => setFormData({ ...formData, location: e.target.value })}
              placeholder="e.g., London, UK or Online"
              style={inputStyle}
            />
          </div>

          {/* Coordinates for venues */}
          {formData.category === 'venues' && (
            <div style={{ display: 'flex', gap: '0.5rem', marginBottom: '0.875rem' }}>
              <div style={{ flex: 1 }}>
                <label style={{ display: 'block', marginBottom: '0.3rem', color: '#00ffff', fontSize: '0.8rem' }}>
                  Latitude
                </label>
                <input
                  type="number"
                  step="any"
                  value={formData.latitude}
                  onChange={(e) => setFormData({ ...formData, latitude: e.target.value })}
                  placeholder="e.g., 51.5074"
                  style={inputStyle}
                />
              </div>
              <div style={{ flex: 1 }}>
                <label style={{ display: 'block', marginBottom: '0.3rem', color: '#00ffff', fontSize: '0.8rem' }}>
                  Longitude
                </label>
                <input
                  type="number"
                  step="any"
                  value={formData.longitude}
                  onChange={(e) => setFormData({ ...formData, longitude: e.target.value })}
                  placeholder="e.g., -0.1278"
                  style={inputStyle}
                />
              </div>
            </div>
          )}

          <div style={{ marginBottom: '0.875rem' }}>
            <label style={{ display: 'block', marginBottom: '0.3rem', color: '#00ffff', fontSize: '0.8rem' }}>
              Tags (comma-separated)
            </label>
            <input
              type="text"
              value={formData.tags}
              onChange={(e) => setFormData({ ...formData, tags: e.target.value })}
              placeholder="e.g., techno, house, legendary"
              style={inputStyle}
            />
          </div>

          <div style={{ marginBottom: '1.25rem' }}>
            <label style={{ 
              display: 'flex', 
              alignItems: 'center', 
              gap: '0.6rem',
              cursor: 'pointer',
              color: 'rgba(255, 255, 255, 0.8)',
              fontSize: '0.85rem'
            }}>
              <input
                type="checkbox"
                checked={formData.is_featured}
                onChange={(e) => setFormData({ ...formData, is_featured: e.target.checked })}
                style={{ width: '16px', height: '16px', accentColor: '#00ffff' }}
              />
              <span>⭐ Mark as Featured</span>
            </label>
          </div>

          <button
            type="submit"
            disabled={loading}
            style={{
              width: '100%',
              padding: '0.75rem',
              borderRadius: '10px',
              border: 'none',
              background: 'linear-gradient(135deg, #00ffff, #0080ff)',
              color: '#000',
              fontSize: '0.9rem',
              fontWeight: '700',
              cursor: loading ? 'not-allowed' : 'pointer',
              opacity: loading ? 0.7 : 1,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              gap: '0.5rem'
            }}
          >
            {loading ? <Loader2 size={16} className="animate-spin" /> : null}
            {loading ? 'Adding...' : 'Add Resource'}
          </button>
        </form>
      </div>
    </div>,
    document.body
  );
};

// Suggest Resource Modal (Regular Users)
const SuggestResourceModal = ({ category, categories, onClose, onSuccess }) => {
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    category: category,
    external_url: '',
    location: ''
  });
  const [loading, setLoading] = useState(false);

  const API = process.env.REACT_APP_BACKEND_URL || '';

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      const token = localStorage.getItem('beatseek_token');
      
      const response = await fetch(`${API}/api/directory/suggestions`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify(formData)
      });

      if (response.ok) {
        onSuccess();
      } else {
        const error = await response.json();
        alert(`Error: ${error.detail}`);
      }
    } catch (error) {
      console.error('Error submitting suggestion:', error);
      alert('Failed to submit suggestion');
    } finally {
      setLoading(false);
    }
  };

  return ReactDOM.createPortal(
    <div
      style={{
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        background: 'rgba(0, 0, 0, 0.9)',
        backdropFilter: 'blur(10px)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        zIndex: 10003,
        padding: '1rem'
      }}
      onClick={onClose}
    >
      <div
        style={{
          maxWidth: '420px',
          width: '100%',
          background: 'rgba(26, 26, 46, 0.98)',
          borderRadius: '16px',
          border: '2px solid #ff00ff',
          maxHeight: '85vh',
          overflow: 'auto'
        }}
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div style={{
          padding: '1rem 1.25rem',
          borderBottom: '1px solid rgba(255, 0, 255, 0.2)',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center'
        }}>
          <h2 style={{ margin: 0, color: '#ff00ff', fontSize: '1.1rem' }}>
            Suggest a Resource
          </h2>
          <button
            onClick={onClose}
            style={{
              width: '32px',
              height: '32px',
              borderRadius: '50%',
              background: 'rgba(255, 0, 128, 0.2)',
              border: '1px solid rgba(255, 0, 128, 0.4)',
              color: '#ff0080',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              cursor: 'pointer'
            }}
          >
            <X size={16} />
          </button>
        </div>

        {/* Info */}
        <div style={{
          padding: '0.75rem 1.25rem',
          background: 'rgba(255, 0, 255, 0.08)',
          borderBottom: '1px solid rgba(255, 0, 255, 0.15)',
          fontSize: '0.8rem',
          color: 'rgba(255, 255, 255, 0.7)'
        }}>
          📝 Your suggestion will be reviewed before being added.
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit} style={{ padding: '1.25rem' }}>
          <div style={{ marginBottom: '0.875rem' }}>
            <label style={{ display: 'block', marginBottom: '0.3rem', color: '#ff00ff', fontSize: '0.8rem' }}>
              Category
            </label>
            <select
              value={formData.category}
              onChange={(e) => setFormData({ ...formData, category: e.target.value })}
              style={inputStyle}
            >
              {categories.map((cat) => (
                <option key={cat.id} value={cat.id} style={{ background: '#1a1a2e', color: '#fff' }}>{cat.name}</option>
              ))}
            </select>
          </div>

          <div style={{ marginBottom: '0.875rem' }}>
            <label style={{ display: 'block', marginBottom: '0.3rem', color: '#ff00ff', fontSize: '0.8rem' }}>
              Name *
            </label>
            <input
              type="text"
              value={formData.name}
              onChange={(e) => setFormData({ ...formData, name: e.target.value })}
              required
              placeholder="Name of the resource"
              style={inputStyle}
            />
          </div>

          <div style={{ marginBottom: '0.875rem' }}>
            <label style={{ display: 'block', marginBottom: '0.3rem', color: '#ff00ff', fontSize: '0.8rem' }}>
              Description *
            </label>
            <textarea
              value={formData.description}
              onChange={(e) => setFormData({ ...formData, description: e.target.value })}
              required
              placeholder="Why is this resource valuable?"
              rows={2}
              style={{ ...inputStyle, resize: 'vertical' }}
            />
          </div>

          <div style={{ marginBottom: '0.875rem' }}>
            <label style={{ display: 'block', marginBottom: '0.3rem', color: '#ff00ff', fontSize: '0.8rem' }}>
              Website URL *
            </label>
            <input
              type="url"
              value={formData.external_url}
              onChange={(e) => setFormData({ ...formData, external_url: e.target.value })}
              required
              placeholder="https://..."
              style={inputStyle}
            />
          </div>

          <div style={{ marginBottom: '1.25rem' }}>
            <label style={{ display: 'block', marginBottom: '0.3rem', color: '#ff00ff', fontSize: '0.8rem' }}>
              Location (optional)
            </label>
            <input
              type="text"
              value={formData.location}
              onChange={(e) => setFormData({ ...formData, location: e.target.value })}
              placeholder="e.g., New York, USA or Online"
              style={inputStyle}
            />
          </div>

          <button
            type="submit"
            disabled={loading}
            style={{
              width: '100%',
              padding: '0.75rem',
              borderRadius: '10px',
              border: 'none',
              background: 'linear-gradient(135deg, #ff00ff, #8000ff)',
              color: '#fff',
              fontSize: '0.9rem',
              fontWeight: '700',
              cursor: loading ? 'not-allowed' : 'pointer',
              opacity: loading ? 0.7 : 1,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              gap: '0.5rem'
            }}
          >
            {loading ? <Loader2 size={16} className="animate-spin" /> : null}
            {loading ? 'Submitting...' : 'Submit Suggestion'}
          </button>
        </form>
      </div>
    </div>,
    document.body
  );
};

export default DirectoryResourcesSection;
